import { useState } from "react";
import { fmt, TOOL_LINE_COLORS, engineLineColor } from "@/lib/format";
import { itemValue, itemTotal } from "@/lib/toolUsageDisplay";

// 도구 × 기간 히트맵 (2026-07-24 리디자인, v2)
// GitHub Contribution 스타일 — 셀 간 gap + 둥근 모서리 + 상단 미니 총합 영역 차트.
// 행=도구(사용량순), 열=기간. 색 농도로 한눈에 패턴 파악.

// 색상 보간: 0 → 투명, max → 진한 브랜드 블루. 5단계 계단식.
function heatColor(value, max) {
  if (!max || !value) return "rgba(241,245,249,0.6)"; // 빈 셀도 은은한 배경
  const ratio = Math.min(value / max, 1);
  if (ratio < 0.2) return "rgba(59,130,246,0.12)";
  if (ratio < 0.4) return "rgba(59,130,246,0.28)";
  if (ratio < 0.6) return "rgba(59,130,246,0.45)";
  if (ratio < 0.8) return "rgba(59,130,246,0.62)";
  return "rgba(59,130,246,0.82)";
}

function textColor(value, max) {
  if (!max || !value) return "text-ink-300";
  const ratio = Math.min(value / max, 1);
  return ratio >= 0.5 ? "text-white" : "text-ink-600";
}

export default function EngineUsageTrendChart({ series, items, unit = "messages" }) {
  const [hoveredCell, setHoveredCell] = useState(null); // {row, col}
  const [hoveredRow, setHoveredRow] = useState(null);
  const rows = series || [];

  if (!items || items.length === 0) {
    return <div className="rounded-xl border border-ink-100 bg-white p-6 text-center text-sm text-ink-400">표시할 도구/엔진을 선택해 주세요</div>;
  }
  if (rows.length === 0) {
    return <div className="rounded-xl border border-ink-100 bg-white p-6 text-center text-sm text-ink-400">데이터 없음</div>;
  }

  // 시간순 정렬 (오래된 → 최신)
  const timeBuckets = [...rows].reverse();

  // 도구를 총 사용량 순으로 정렬
  const sortedItems = [...items].sort((a, b) => itemTotal(rows, b, unit) - itemTotal(rows, a, unit));

  // 전체 최대값 (히트맵 스케일 기준)
  const allValues = timeBuckets.flatMap((r) => sortedItems.map((item) => itemValue(r, item, unit)));
  const maxVal = Math.max(1, ...allValues);

  // 행 합계
  const rowTotals = sortedItems.map((item) => itemTotal(rows, item, unit));
  const grandTotal = rowTotals.reduce((s, v) => s + v, 0);

  // 피크 셀 탐지 제거됨 (2026-07-24) — 히트맵 색 농도 자체가 피크를 보여주므로 노란 점은 시각 노이즈

  return (
    <div className="rounded-xl border border-ink-100 bg-white overflow-visible">
      {/* 헤더 */}
      <div className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
        <span className="text-[13px] font-semibold text-ink-700">
          도구 × 기간 히트맵
        </span>
        <div className="flex items-center gap-3">
          {/* 범례 */}
          <div className="flex items-center gap-1.5 text-[11px] text-ink-400">
            <span>적음</span>
            <div className="flex h-3.5 gap-0.5">
              {["rgba(59,130,246,0.12)", "rgba(59,130,246,0.28)", "rgba(59,130,246,0.45)", "rgba(59,130,246,0.62)", "rgba(59,130,246,0.82)"].map((c) => (
                <div key={c} className="w-5 rounded" style={{ backgroundColor: c }} />
              ))}
            </div>
            <span>많음</span>
          </div>
        </div>
      </div>

      {/* 히트맵 본체 — GitHub 잔디 스타일 셀 gap */}
      <div className="overflow-x-auto overflow-y-visible px-3 py-3">
        <div style={{ minWidth: `${7 + timeBuckets.length * 2.8}rem` }}>
          {/* 기간 헤더 */}
          <div className="flex items-center mb-1.5" style={{ paddingLeft: "8rem" }}>
            {timeBuckets.map((r) => (
              <div
                key={r.bucket}
                className="flex-1 text-center text-[10px] font-medium text-ink-400 truncate px-0.5"
              >
                {r.bucketLabel || r.bucket}
              </div>
            ))}
            <div className="w-16 text-right text-[10px] font-semibold text-ink-500 pl-2">합계</div>
          </div>

          {/* 도구 행 */}
          {sortedItems.map((item, rowIdx) => {
            const color = item.kind === "group"
              ? TOOL_LINE_COLORS[item.superGroup] || "#6366f1"
              : engineLineColor(item.keys[0]?.split("|")[0], item.keys[0]?.split("|")[1]) || "#6366f1";
            const total = rowTotals[rowIdx];
            const pct = grandTotal > 0 ? ((total / grandTotal) * 100).toFixed(1) : "0";

            return (
              <div
                key={item.id}
                className={`flex items-center mb-1 rounded-lg transition-colors ${hoveredRow === rowIdx ? "bg-ink-50/70" : ""}`}
                onMouseEnter={() => setHoveredRow(rowIdx)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                {/* 도구명 */}
                <div className="w-32 shrink-0 flex items-center gap-2 px-2 py-1">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-[12px] font-medium text-ink-700 truncate">{item.label}</span>
                </div>

                {/* 히트맵 셀들 */}
                <div className="flex flex-1 gap-1">
                  {timeBuckets.map((r, colIdx) => {
                    const val = itemValue(r, item, unit);
                    const isHovered = hoveredCell?.row === rowIdx && hoveredCell?.col === colIdx;

                    return (
                      <div
                        key={r.bucket}
                        className="relative flex-1"
                        onMouseEnter={() => setHoveredCell({ row: rowIdx, col: colIdx })}
                        onMouseLeave={() => setHoveredCell(null)}
                      >
                        <div
                          className={`h-8 w-full rounded-md flex items-center justify-center transition-all duration-150 ${
                            isHovered ? "ring-2 ring-brand-400 scale-110 z-10" : ""
                          }`}
                          style={{ backgroundColor: heatColor(val, maxVal) }}
                        >
                          {/* 호버 시 또는 큰 값일 때 숫자 표시 */}
                          {val > 0 && (isHovered || val >= maxVal * 0.5) && (
                            <span className={`text-[9px] font-bold tabular-nums ${textColor(val, maxVal)}`}>
                              {val >= 1000 ? fmt.compact(val) : val}
                            </span>
                          )}
                        </div>
                        {/* 호버 툴팁 */}
                        {isHovered && (
                          <div className={`absolute left-1/2 -translate-x-1/2 z-30 whitespace-nowrap rounded-lg bg-ink-800 px-3 py-2 text-[11px] text-white shadow-xl pointer-events-none ${
                            rowIdx <= 1 ? "top-full mt-2" : "bottom-full mb-2"
                          }`}>
                            <div className="font-semibold">{item.label}</div>
                            <div className="text-ink-300">{r.bucketLabel || r.bucket}</div>
                            <div className="mt-1 text-[13px] font-bold">{fmt.int(val)} {unit === "sessions" ? "세션" : "메시지"}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* 행 합계 */}
                <div className="w-16 shrink-0 text-right pl-2 pr-2">
                  <div className="text-[12px] font-bold tabular-nums text-ink-800">{fmt.int(total)}</div>
                  <div className="text-[9px] text-ink-400">{pct}%</div>
                </div>
              </div>
            );
          })}

          {/* 하단 열 합계 바 */}
          <div className="flex items-center mt-2 pt-2 border-t border-ink-100" style={{ paddingLeft: "8rem" }}>
            <div className="flex flex-1 gap-1">
              {timeBuckets.map((r) => {
                const colTotal = sortedItems.reduce((s, item) => s + itemValue(r, item, unit), 0);
                const colRatio = grandTotal > 0 ? colTotal / Math.max(...timeBuckets.map((b) => sortedItems.reduce((s, it) => s + itemValue(b, it, unit), 0)), 1) : 0;
                return (
                  <div key={r.bucket} className="flex-1 flex flex-col items-center gap-0.5">
                    {/* 미니 바 */}
                    <div className="w-full h-1.5 rounded-full bg-ink-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-brand-400"
                        style={{ width: `${colRatio * 100}%` }}
                      />
                    </div>
                    <span className="text-[9px] font-semibold tabular-nums text-ink-500">
                      {colTotal >= 1000 ? fmt.compact(colTotal) : colTotal}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="w-16 shrink-0 text-right pl-2 pr-2">
              <span className="text-[12px] font-bold tabular-nums text-ink-900">{fmt.int(grandTotal)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
